import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { currentPeriod, P } from '@hrms/shared';
import { useAuth } from '../../../auth/AuthProvider';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/errors';
import { openFile } from '../../../lib/files';
import { useBreakpoint } from '../../../lib/hooks';
import { todayISO } from '../../../lib/date';
import { useParams } from '../../../navigation/nav';
import { PeriodSelect, SideMenu } from '../components/misc';
import { useOrgOptions } from '../components/orgOptions';
import { Alert, BarChart, Button, Card, DataTable, DateField, EmptyState, Loading, PageHeader, Row, Screen, Select, Stack, Text, useToast } from '../../../ui';

/** Buttons default to alignSelf flex-start; bottom-align them with labelled fields. */
const END = { alignSelf: 'flex-end' as const };

type ParamKind = 'range' | 'period' | 'org';
const REPORTS: { key: string; name: string; desc: string; params: ParamKind[]; perms: string[] }[] = [
  { key: 'headcount', name: 'Headcount', desc: 'Active employees by department, location and grade', params: ['org'], perms: [P.REPORTS_FULL, P.REPORTS_TEAM] },
  { key: 'attrition', name: 'Attrition', desc: 'Exits and attrition rate over a date range', params: ['range', 'org'], perms: [P.REPORTS_FULL] },
  { key: 'leave-utilisation', name: 'Leave utilisation', desc: 'Leave taken vs available by type', params: ['range', 'org'], perms: [P.REPORTS_FULL, P.REPORTS_TEAM] },
  { key: 'attendance-summary', name: 'Attendance summary', desc: 'Present, absent, late and LOP per employee', params: ['period', 'org'], perms: [P.REPORTS_FULL, P.REPORTS_TEAM] },
  { key: 'payroll-register', name: 'Payroll register', desc: 'Component-wise earnings and deductions for a period', params: ['period', 'org'], perms: [P.REPORTS_FULL, P.REPORTS_PAYROLL] },
];
const PAGE = 100;
type Params = { date_from: string; date_to: string; period: string; department_id: number | ''; location_id: number | '' };

/** Standard reports with filters, table, chart and CSV export. Optional param { key }. */
export function ReportsScreen() {
  const { can } = useAuth();
  const toast = useToast();
  const { isTablet } = useBreakpoint();
  const keyParam = useParams<{ key: string }>().key;
  const available = REPORTS.filter((r) => can(...r.perms));
  const [key, setKey] = useState<string | null>(keyParam && available.some((r) => r.key === keyParam) ? keyParam : available[0]?.key ?? null);
  useEffect(() => {
    if (keyParam && REPORTS.some((r) => r.key === keyParam)) setKey(keyParam);
  }, [keyParam]);
  const def = REPORTS.find((r) => r.key === key);
  const org = useOrgOptions();
  const y = new Date().getFullYear();
  const [params, setParams] = useState<Params>({ date_from: `${y}-01-01`, date_to: todayISO(), period: currentPeriod(), department_id: '', location_id: '' });
  const [runParams, setRunParams] = useState<Params | null>(null);
  const [limit, setLimit] = useState(PAGE);
  const active: Record<string, string | number> = {};
  if (def && runParams) {
    if (def.params.includes('range')) Object.assign(active, { date_from: runParams.date_from, date_to: runParams.date_to });
    if (def.params.includes('period')) active.period = runParams.period;
    if (def.params.includes('org')) {
      if (runParams.department_id) active.department_id = runParams.department_id;
      if (runParams.location_id) active.location_id = runParams.location_id;
    }
  }
  const q = useQuery({ queryKey: ['reports', key, active], queryFn: () => api.reports.run(key!, active), enabled: !!key && !!runParams });
  const r = q.data;
  const numCol = r?.columns.find((c, i) => i > 0 && r.rows.length > 0 && r.rows.every((row) => typeof row[c.key] === 'number' || row[c.key] == null));
  const chartable = !!r && !!numCol && r.rows.length > 1 && r.rows.length <= 30;
  const set = <K extends keyof Params>(k: K, v: Params[K]) => setParams({ ...params, [k]: v });
  const pick = (k: string) => {
    setKey(k);
    setRunParams(null);
    setLimit(PAGE);
  };
  const csv = () => def && openFile(api.reports.csvPath(def.key), { format: 'csv', ...active }).catch(() => toast.error('Could not start the download'));

  const menu = <SideMenu title="Reports" value={key} onChange={pick} items={available.map((x) => ({ key: x.key, title: x.name, subtitle: x.desc }))} emptyTitle="No reports for your role" testID="reports-menu" />;
  const body = !def ? (
    <Card>
      <EmptyState icon="chart" title="No reports available for your role" />
    </Card>
  ) : (
    <Stack>
      <Card title={def.name} subtitle={def.desc}>
        <Row align="flex-end" gap={10}>
          {def.params.includes('range') && (
            <>
              <View style={{ minWidth: 160 }}>
                <DateField label="From" value={params.date_from} onChange={(v) => set('date_from', v)} />
              </View>
              <View style={{ minWidth: 160 }}>
                <DateField label="To" value={params.date_to} min={params.date_from} onChange={(v) => set('date_to', v)} />
              </View>
            </>
          )}
          {def.params.includes('period') && (
            <View style={{ minWidth: 180 }}>
              <PeriodSelect value={params.period} onChange={(v) => set('period', v)} />
            </View>
          )}
          {def.params.includes('org') && (
            <>
              <Select<number | ''> containerStyle={{ minWidth: 170 }} label="Department" value={params.department_id} options={[{ value: '', label: 'All' }, ...org.departments]} onChange={(v) => set('department_id', v)} />
              <Select<number | ''> containerStyle={{ minWidth: 170 }} label="Location" value={params.location_id} options={[{ value: '', label: 'All' }, ...org.locations]} onChange={(v) => set('location_id', v)} />
            </>
          )}
          <Button variant="primary" icon="play" title="Run" loading={q.isFetching} onPress={() => { setRunParams({ ...params }); setLimit(PAGE); }} testID="report-run" style={END} />
          <Button icon="download" title="CSV" disabled={!runParams} onPress={csv} testID="report-csv" style={END} />
        </Row>
      </Card>
      {q.isError && <Alert tone="danger">{errorMessage(q.error)}</Alert>}
      {q.isLoading && !!runParams && <Loading />}
      {r && chartable && (
        <Card title={`${numCol!.label} by ${r.columns[0].label}`}>
          <BarChart data={r.rows.map((row) => ({ label: String(row[r.columns[0].key] ?? '—'), value: Number(row[numCol!.key] ?? 0) }))} format={(n) => n.toLocaleString('en-IN')} />
        </Card>
      )}
      {r && (
        <>
          <Text variant="small">{`${r.rows.length} rows`}</Text>
          <DataTable
            rows={r.rows.slice(0, limit)}
            keyExtractor={(_, i) => i}
            emptyTitle="No rows"
            testID="report-table"
            columns={r.columns.map((c, ci) => ({
              key: c.key,
              title: c.label,
              width: ci === 0 ? 170 : 120,
              flex: ci === 0 ? 2 : 1,
              align: typeof r.rows[0]?.[c.key] === 'number' ? ('right' as const) : undefined,
              sortValue: (row: Record<string, string | number | null>) => row[c.key],
              render: (row: Record<string, string | number | null>) => {
                const v = row[c.key];
                return v == null ? '—' : typeof v === 'number' ? v.toLocaleString('en-IN') : v;
              },
            }))}
          />
          {r.rows.length > limit && <Button title={`Show more (${r.rows.length - limit} left)`} onPress={() => setLimit(limit + PAGE)} />}
        </>
      )}
      {!runParams && (
        <Card>
          <EmptyState icon="play" title="Choose filters and run the report" />
        </Card>
      )}
    </Stack>
  );
  return (
    <Screen testID="reports">
      <PageHeader title="Reports" />
      {isTablet ? (
        <View style={{ flexDirection: 'row', gap: 16, alignItems: 'flex-start' }}>
          <View style={{ width: 260 }}>{menu}</View>
          <View style={{ flex: 1, minWidth: 0 }}>{body}</View>
        </View>
      ) : (
        <Stack>
          <Select label="Report" value={key} options={available.map((x) => ({ value: x.key, label: x.name }))} onChange={pick} />
          {body}
        </Stack>
      )}
    </Screen>
  );
}
